import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
    Dialog,
    Button,
    TextField,
    Radio,
    RadioGroup,
    FormControlLabel,
    Box,
    Autocomplete,
    Checkbox,
    CircularProgress,
    IconButton,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { designTokens } from '../../../theme';
import StoreContext from '../../../react-core-lib/components/app/store-context';
import UserApi from '../../../rest-api/user-api';
import { useSnackbar } from '../SnackbarProvider';
import BlockingSpinner from '../../UIControls/BlockingSpinner';
import {
    formatPatientAutocompleteLabel,
    formatPatientAutocompleteSearchText,
    patientAutocompleteNoOptionsText,
    patientAutocompleteSx,
    renderPatientAutocompleteOption,
} from '../../UIControls/patient-autocomplete-option';
import {
    CLINIC_PATIENT_ID_LABEL,
    PATIENT_EMAIL_LABEL,
    PATIENT_NAME_LABEL,
    PATIENT_SURNAME_LABEL,
} from '../../helper/patientLabels';

const SaveRegularHandoutModal = ({ open, onClose, handoutData, existingSavedHandout = null, onSaved }) => {
    const { userDataStore, authStore, savedHandoutStore } = useContext(StoreContext);
    const showSnackbar = useSnackbar();

    const [saving, setSaving] = useState(false);
    const [notes, setNotes] = useState('');
    const [patientList, setPatientList] = useState([]);
    const [patientListLoading, setPatientListLoading] = useState(false);
    const [patientType, setPatientType] = useState('existing');
    const [selectedPatient, setSelectedPatient] = useState('');
    const [manualPatientName, setManualPatientName] = useState('');
    const [patientSurname, setPatientSurname] = useState('');
    const [patientEmail, setPatientEmail] = useState('');
    const [clinicPatientId, setClinicPatientId] = useState('');
    const [includeAdditionalInfo, setIncludeAdditionalInfo] = useState(false);

    const isStudent = userDataStore?.subscriptionData?.accountType === 'Student';
    const isPatientSavedContext = Boolean(existingSavedHandout?.savedHandoutId && existingSavedHandout?.patientId);

    useEffect(() => {
        if (open) {
            setNotes('');
            setPatientType('existing');
            setSelectedPatient('');
            setManualPatientName('');
            setPatientSurname('');
            setPatientEmail('');
            setClinicPatientId('');
            setIncludeAdditionalInfo(false);
        }
    }, [open]);

    const fetchPatientList = useCallback(async () => {
        setPatientListLoading(true);
        try {
            const response = await UserApi.getPatientsByFilter(authStore.idToken, {
                filterType: 'patientStatus',
                filterValue: 'active',
            });

            if (response?.patients) {
                setPatientList(response.patients);
            }
        } catch (error) {
            console.error('Failed to fetch patients:', error);
        } finally {
            setPatientListLoading(false);
        }
    }, [authStore.idToken]);

    useEffect(() => {
        if (!isStudent && open) {
            fetchPatientList();
        }
    }, [fetchPatientList, isStudent, open]);

    const handleSave = async () => {
        if (saving) return;
        setSaving(true);

        if (!isStudent) {
            if (patientType === 'existing' && !selectedPatient) {
                showSnackbar('Please select an existing patient.', 'error');
                setSaving(false);
                return;
            }
            if (patientType === 'new') {
                if (!manualPatientName.trim()) {
                    showSnackbar(`${PATIENT_NAME_LABEL} is required.`, 'error');
                    setSaving(false);
                    return;
                }

                if (!patientSurname.trim()) {
                    showSnackbar(`${PATIENT_SURNAME_LABEL} is required.`, 'error');
                    setSaving(false);
                    return;
                }

                if (patientEmail.trim()) {
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(patientEmail.trim())) {
                        showSnackbar('Please provide a valid email address.', 'error');
                        setSaving(false);
                        return;
                    }
                }
            }
        }

        if (isStudent) {
            showSnackbar('Patient records are not available on your current plan.', 'error');
            setSaving(false);
            return;
        }

        if (isPatientSavedContext && patientType === 'existing' && selectedPatient === existingSavedHandout.patientId) {
            showSnackbar('Please select a different patient.', 'warning');
            setSaving(false);
            return;
        }

        try {
            const clinicianData = await UserApi.getClinicianData(authStore.idToken);
            if (!clinicianData?.userId) {
                showSnackbar('Failed to retrieve user data. Please try again.', 'error');
                setSaving(false);
                return;
            }

            let finalPatientName = null;
            let finalPatientSurname = null;
            let finalPatientEmail = null;
            let finalClinicPatientId = null;
            let finalPatientId = null;

            if (patientType === 'existing') {
                const existing = patientList.find((p) => p.patientId === selectedPatient);
                finalPatientName = existing ? existing.patientName : '';
                finalPatientSurname = existing ? existing.patientSurname : '';
                finalPatientEmail = existing ? existing.patientEmail : null;
                finalClinicPatientId = existing ? existing.clinicPatientId : null;
                finalPatientId = selectedPatient;
            } else {
                finalPatientName = manualPatientName.trim();
                finalPatientSurname = patientSurname.trim();
                finalPatientEmail = patientEmail.trim() || null;
                finalClinicPatientId = clinicPatientId.trim() || null;

                const newPatientData = {
                    patientName: finalPatientName,
                    patientSurname: finalPatientSurname,
                };

                if (finalClinicPatientId) newPatientData.clinicPatientId = finalClinicPatientId;
                if (finalPatientEmail) newPatientData.patientEmail = finalPatientEmail;

                const patientResp = await UserApi.createOrUpdatePatient(authStore.idToken, newPatientData);
                if (!patientResp?.patientId) {
                    showSnackbar('Failed to create patient.', 'error');
                    setSaving(false);
                    return;
                }

                finalPatientId = patientResp.patientId;
            }

            const payload = {
                savedHandoutId: null,
                baseContentId: handoutData.contentId,
                baseLastUpdated: handoutData.lastUpdated,
                savedStatus: 'active',
                notes: notes.trim() || null,
                updatedById: userDataStore.userId,
                updatedByFirstName: userDataStore.userData.firstName || null,
                updatedByLastName: userDataStore.userData.lastName || null,
                handoutContentName: handoutData.contentName || null,
                handoutAlphabeticalName: handoutData.alphabeticalName || null,
                handoutDocumentTitle: handoutData.contentName || null,
                baseContentIdUserIdCompositeKey: `${handoutData.contentId}#${clinicianData.userId}`,
                patientId: finalPatientId,
                patientName: finalPatientName,
                patientSurname: finalPatientSurname,
                patientEmail: finalPatientEmail,
                clinicPatientId: finalClinicPatientId,
            };

            const response = await UserApi.createOrUpdateSavedHandout(authStore.idToken, payload);

            if (response?.savedHandoutId) {
                const fullSavedHandout = {
                    savedHandoutId: response.savedHandoutId,
                    userId: clinicianData.userId,
                    baseContentId: payload.baseContentId,
                    baseLastUpdated: payload.baseLastUpdated,
                    notes: payload.notes,
                    savedStatus: response.savedStatus,
                    lastUpdated: Date.now(),
                    updatedBy: userDataStore.userData.emailAddress,
                    updatedById: userDataStore.userId,
                    updatedByFirstName: userDataStore.userData.firstName || null,
                    updatedByLastName: userDataStore.userData.lastName || null,
                    handoutContentName: payload.handoutContentName,
                    handoutAlphabeticalName: payload.handoutAlphabeticalName,
                    handoutDocumentTitle: payload.handoutDocumentTitle,
                    baseContentIdUserIdCompositeKey: payload.baseContentIdUserIdCompositeKey,
                    patientId: payload.patientId,
                    patientName: payload.patientName,
                    patientSurname: payload.patientSurname,
                    patientEmail: payload.patientEmail,
                    clinicPatientId: payload.clinicPatientId,
                };

                await savedHandoutStore.addOrUpdateSavedHandout(fullSavedHandout);

                showSnackbar('Handout saved successfully.', 'success');
                if (onSaved) onSaved(fullSavedHandout);
                onClose();
            } else {
                showSnackbar('Failed to save handout.', 'error');
            }
        } catch (error) {
            console.error('Failed to save regular handout:', error);
            showSnackbar('Failed to save handout. Please try again.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const inputFieldSx = {
        '& .MuiOutlinedInput-root': {
            height: { xs: 'auto', sm: 52 },
            minHeight: { xs: '36px', sm: 'auto' },
            borderRadius: { xs: '8px', sm: '14px' },
            backgroundColor: '#FFFFFF',
            '& fieldset': { borderColor: '#E5E5E5', borderWidth: { xs: '0.6px', sm: '1.5px' } },
            '&:hover fieldset': { borderColor: '#E5E5E5' },
            '&.Mui-focused fieldset': { borderColor: '#FF669B' },
        },
    };

    const inputFieldPropsSx = {
        fontFamily: 'Inter, sans-serif',
        fontSize: { xs: '14px', sm: '16px' },
        letterSpacing: { xs: '-0.56px', sm: '-0.64px' },
        color: '#616063',
        borderRadius: { xs: '8px', sm: '14px' },
        height: { xs: 'auto', sm: '52px' },
        minHeight: { xs: '36px', sm: 'auto' },
        alignItems: 'center',
    };

    const radioLabelSx = {
        '& .MuiFormControlLabel-label': {
            fontFamily: 'Inter, sans-serif',
            fontSize: { xs: 14, sm: 18 },
            lineHeight: { xs: '14px', sm: '18px' },
            letterSpacing: { xs: '-0.56px', sm: '-0.72px' },
            color: designTokens.colors.textSecondary,
        },
    };

    const saveButtonSx = {
        backgroundColor: '#FF669B',
        color: '#FFFFFF',
        borderRadius: { xs: '100px', sm: 36 },
        width: { xs: '100%', sm: '160px' },
        height: { xs: '35px', sm: '44px' },
        fontSize: { xs: 14, sm: 16 },
        fontWeight: 500,
        lineHeight: { xs: '14px', sm: '16px' },
        letterSpacing: { xs: '-0.28px', sm: '-0.64px' },
        textTransform: 'none',
        '&:hover': { backgroundColor: '#e65588' },
        '&.Mui-disabled': { backgroundColor: 'rgba(255, 102, 155, 0.5)', color: '#FFFFFF' },
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    borderRadius: '30px !important',
                    padding: { xs: '30px 20px', sm: '30px 28px' },
                    width: { xs: 'calc(100% - 32px)', sm: '711px' },
                    maxWidth: { xs: 'calc(100% - 32px)', sm: '711px' },
                    maxHeight: 'calc(100dvh - 32px)',
                    boxSizing: 'border-box',
                    overflowY: 'auto',
                },
            }}
            maxWidth={false}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: { xs: '8px', sm: '24px' },
                    }}
                >
                    <Typography
                        sx={{
                            fontFamily: 'Inter, sans-serif',
                            fontWeight: 600,
                            fontSize: { xs: 26, sm: 32 },
                            lineHeight: { xs: '26px', sm: '32px' },
                            letterSpacing: { xs: '-1.04px', sm: '-1.28px' },
                            color: designTokens.colors.textPrimary,
                            pr: '44px',
                        }}
                    >
                        Save to patient
                    </Typography>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            p: 0,
                            width: { xs: '28px', sm: '36px' },
                            height: { xs: '28px', sm: '36px' },
                            background: 'rgb(246, 246, 246)',
                            borderRadius: { xs: '11.928px', sm: '50%' },
                            flexShrink: 0,
                        }}
                    >
                        <CloseIcon sx={{ width: { xs: '16px', sm: '24px' }, height: { xs: '16px', sm: '24px' } }} />
                    </IconButton>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {isPatientSavedContext && (
                        <Typography
                            sx={{
                                fontFamily: 'Inter, sans-serif',
                                fontSize: { xs: 13, sm: 14 },
                                color: designTokens.colors.textSecondary,
                            }}
                        >
                            This handout has already been saved to a patient record. If you want to save it to a
                            different patient, then please choose a different existing patient or create a new patient.
                        </Typography>
                    )}

                    <RadioGroup
                        row
                        value={patientType}
                        onChange={(e) => setPatientType(e.target.value)}
                        sx={{ gap: { xs: 0.5, sm: 1 } }}
                    >
                        <FormControlLabel
                            value="existing"
                            control={<Radio sx={{ color: '#FF669B', '&.Mui-checked': { color: '#FF669B' } }} />}
                            label="Existing patient"
                            sx={radioLabelSx}
                        />
                        <FormControlLabel
                            value="new"
                            control={<Radio sx={{ color: '#FF669B', '&.Mui-checked': { color: '#FF669B' } }} />}
                            label="New patient"
                            sx={radioLabelSx}
                        />
                    </RadioGroup>

                    {patientType === 'existing' ? (
                        <Autocomplete
                            options={patientList}
                            loading={patientListLoading}
                            loadingText="Loading patients..."
                            getOptionLabel={formatPatientAutocompleteLabel}
                            getOptionDisabled={(option) =>
                                isPatientSavedContext && option.patientId === existingSavedHandout?.patientId
                            }
                            value={patientList.find((p) => p.patientId === selectedPatient) || null}
                            onChange={(_, value) => setSelectedPatient(value?.patientId || '')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Select patient"
                                    sx={inputFieldSx}
                                    InputProps={{
                                        ...params.InputProps,
                                        sx: inputFieldPropsSx,
                                        endAdornment: (
                                            <>
                                                {patientListLoading && <CircularProgress color="inherit" size={18} />}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                    inputProps={{ ...params.inputProps, className: 'mp-mask-patient' }}
                                />
                            )}
                            noOptionsText={patientAutocompleteNoOptionsText}
                            renderOption={renderPatientAutocompleteOption}
                            filterOptions={(options, { inputValue }) => {
                                const query = inputValue.trim().toLowerCase();
                                if (!query) return options;
                                return options.filter((patient) =>
                                    formatPatientAutocompleteSearchText(patient).includes(query),
                                );
                            }}
                            sx={patientAutocompleteSx}
                        />
                    ) : (
                        <>
                            <TextField
                                label={PATIENT_NAME_LABEL}
                                required
                                fullWidth
                                value={manualPatientName}
                                onChange={(e) => setManualPatientName(e.target.value)}
                                sx={inputFieldSx}
                                InputProps={{ sx: inputFieldPropsSx }}
                                inputProps={{ className: 'mp-mask-patient' }}
                            />
                            <TextField
                                label={PATIENT_SURNAME_LABEL}
                                required
                                fullWidth
                                value={patientSurname}
                                onChange={(e) => setPatientSurname(e.target.value)}
                                sx={inputFieldSx}
                                InputProps={{ sx: inputFieldPropsSx }}
                                inputProps={{ className: 'mp-mask-patient' }}
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={includeAdditionalInfo}
                                        onChange={(e) => setIncludeAdditionalInfo(e.target.checked)}
                                    />
                                }
                                label="Add additional patient details"
                                sx={radioLabelSx}
                            />

                            {includeAdditionalInfo && (
                                <>
                                    <TextField
                                        label={PATIENT_EMAIL_LABEL}
                                        fullWidth
                                        value={patientEmail}
                                        onChange={(e) => setPatientEmail(e.target.value)}
                                        sx={inputFieldSx}
                                        InputProps={{ sx: inputFieldPropsSx }}
                                        inputProps={{ className: 'mp-mask-patient' }}
                                    />
                                    <TextField
                                        label={CLINIC_PATIENT_ID_LABEL}
                                        fullWidth
                                        value={clinicPatientId}
                                        onChange={(e) => setClinicPatientId(e.target.value)}
                                        sx={inputFieldSx}
                                        InputProps={{ sx: inputFieldPropsSx }}
                                        inputProps={{ className: 'mp-mask-patient' }}
                                    />
                                </>
                            )}
                        </>
                    )}
                </Box>

                <TextField
                    label="Notes"
                    fullWidth
                    multiline
                    minRows={2}
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    inputProps={{ className: 'mp-mask-patient' }}
                    sx={{
                        mt: 2,
                        '& .MuiOutlinedInput-root': {
                            borderRadius: { xs: '8px', sm: '14px' },
                            backgroundColor: '#FFFFFF',
                            '& fieldset': { borderColor: '#E5E5E5', borderWidth: { xs: '0.6px', sm: '1.5px' } },
                            '&:hover fieldset': { borderColor: '#E5E5E5' },
                            '&.Mui-focused fieldset': { borderColor: '#FF669B' },
                        },
                    }}
                    InputProps={{
                        sx: {
                            fontFamily: 'Inter, sans-serif',
                            fontSize: { xs: '14px', sm: '16px' },
                            letterSpacing: { xs: '-0.56px', sm: '-0.64px' },
                            color: '#616063',
                            borderRadius: { xs: '8px', sm: '14px' },
                        },
                    }}
                />

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: '24px', sm: '28px' } }}>
                    <Button onClick={handleSave} disabled={saving} sx={saveButtonSx}>
                        Save
                    </Button>
                </Box>
            </Box>
            <BlockingSpinner open={saving} />
        </Dialog>
    );
};

export default SaveRegularHandoutModal;
